import gtmIcon from 'assets/images/integrations/gtm.svg';

import type { ConversionRule } from 'modules/pixel/pixelApiTypes';

import { CodeBlock } from '../CodeBlock';
import type { IntegrationContext, IntegrationSnippet, PixelIntegration } from './types';

/**
 * Google Tag Manager — install the universal pixel as a Custom HTML tag fired on All Pages.
 * Per-rule snippets fire via {@code CMPIXEL.fire(...)} from additional Custom HTML tags that
 * use dataLayer events or page-path triggers.
 */
export const gtmIntegration: PixelIntegration = {
  id: 'gtm',
  label: 'Google Tag Manager',
  description:
    'Install the universal pixel as a Custom HTML tag fired on All Pages. Use dataLayer events or page-path triggers to fire per-rule conversions.',
  icon: <img src={gtmIcon} alt="" className="h-4 w-4" />,
  docsHref: 'https://support.google.com/tagmanager/answer/6107167',
  renderSetup: (ctx) => (
    <div className="flex flex-col gap-4">
      <Step number={1} title="Add the universal pixel as a Custom HTML tag">
        <p className="mb-2 text-base-xs text-primary-gray-500">
          In GTM: <strong>Tags → New → Tag Configuration → Custom HTML</strong>. Paste:
        </p>
        <CodeBlock language="html" code={ctx.pixelScriptTag} />
      </Step>
      <Step number={2} title="Trigger on All Pages, once per page">
        <p className="text-base-xs text-primary-gray-500">
          Under <strong>Triggering</strong>, pick <strong>All Pages</strong> with{' '}
          <em>Once per page</em>. Add Consent Mode conditions if your site requires it.
        </p>
      </Step>
      <Step number={3} title="Preview, then Publish">
        <p className="text-base-xs text-primary-gray-500">
          Use GTM <strong>Preview</strong> to confirm the tag fires on every page. Once verified,
          click <strong>Submit → Publish</strong>.
        </p>
      </Step>
      <Step number={4} title="(Optional) Fire conversions from dataLayer / page triggers">
        <p className="text-base-xs text-primary-gray-500">
          For each rule below, add a separate Custom HTML tag with the snippet and pick a trigger
          that matches your event (e.g. dataLayer event{' '}
          <code className="rounded bg-primary-gray-100 px-1 font-mono text-[11px]">purchase</code>{' '}
          or page-path contains{' '}
          <code className="rounded bg-primary-gray-100 px-1 font-mono text-[11px]">/thank-you</code>
          ).
        </p>
      </Step>
    </div>
  ),
  buildSnippetForRule: (rule, ctx) => buildGtmSnippet(rule, ctx),
};

function Step({
  number,
  title,
  children,
}: {
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-electric-100 text-[11px] font-semibold text-primary-electric-700">
          {number}
        </span>
        <h4 className="text-base-sm font-medium text-primary-gray-900">{title}</h4>
      </div>
      <div className="pl-7">{children}</div>
    </div>
  );
}

function buildGtmSnippet(rule: ConversionRule, ctx: IntegrationContext): IntegrationSnippet {
  const lines: string[] = [`  'category': ${JSON.stringify(rule.category)}`];

  if (rule.subcategory) lines.push(`  'subcategory': ${JSON.stringify(rule.subcategory)}`);
  const extractorKeys = rule.extractors ? Object.keys(rule.extractors) : [];

  for (const key of extractorKeys) {
    const placeholder =
      key === 'amount'
        ? '{{Purchase Value}}      // a GTM variable holding order total'
        : key === 'currency'
          ? '\'{{Purchase Currency}}\''
          : key === 'order_number'
            ? '\'{{Order ID}}\''
            : '\'\'';

    lines.push(`  '${key}': ${placeholder}`);
  }
  const body = lines.join(',\n');

  const code = [
    '<script>',
    '// Fires when GTM evaluates this tag\'s trigger.',
    'if (window.CMPIXEL && CMPIXEL.fire) {',
    `  CMPIXEL.fire('${ctx.pixelCode}', {`,
    body,
    '  });',
    '}',
    '</script>',
  ].join('\n');

  return {
    code,
    language: 'html',
    label: 'Custom HTML tag — paste into a new GTM tag',
    note: 'Pair this tag with a trigger that matches the rule (custom dataLayer event, page-path contains, click selector, …). Pull dynamic values from GTM variables (the {{…}} placeholders) populated by your site\'s dataLayer pushes.',
  };
}
